import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { MdBarChart, MdTrendingUp, MdStar, MdLock, MdVideoLibrary, MdPeople, MdTimer, MdVisibility, MdAutoAwesome } from 'react-icons/md';
import { FaYoutube, FaFacebook, FaTiktok, FaInstagram } from 'react-icons/fa';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { getOverview, getPlatformAnalytics, getChannels, getChannelAnalytics, getUploadStrategy, getVideos } from '../utils/api';

const COLORS = ['#00c853', '#1de9b6', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6'];

const platformConfig = {
  youtube: {
    icon: <FaYoutube style={{ color: '#ff0000', fontSize: '20px' }} />,
    label: 'YouTube',
    color: '#ff0000',
    bg: 'rgba(255,0,0,0.08)',
    border: 'rgba(255,0,0,0.2)'
  },
  facebook: {
    icon: <FaFacebook style={{ color: '#1877f2', fontSize: '20px' }} />,
    label: 'Facebook',
    color: '#1877f2',
    bg: 'rgba(24,119,242,0.08)',
    border: 'rgba(24,119,242,0.2)'
  },
  tiktok: {
    icon: <FaTiktok style={{ color: '#e0e0e0', fontSize: '20px' }} />,
    label: 'TikTok',
    color: '#e0e0e0',
    bg: 'rgba(255,255,255,0.04)',
    border: 'rgba(255,255,255,0.1)'
  },
  instagram: {
    icon: <FaInstagram style={{ color: '#e1306c', fontSize: '20px' }} />,
    label: 'Instagram',
    color: '#e1306c',
    bg: 'rgba(225,48,108,0.08)',
    border: 'rgba(225,48,108,0.2)'
  }
};

const OAuthPlaceholder = ({ metric, value, icon }) => (
  <div style={{
    padding: '16px', background: 'var(--bg-dark)',
    borderRadius: '12px', border: '1px solid var(--border)',
    position: 'relative', overflow: 'hidden'
  }}>
    <div style={{
      position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(2px)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      zIndex: 2, borderRadius: '12px'
    }}>
      <MdLock style={{ fontSize: '20px', color: 'var(--primary)', marginBottom: '6px' }} />
      <span style={{ fontSize: '11px', color: 'var(--text-secondary)', textAlign: 'center', padding: '0 8px' }}>
        Available after OAuth setup
      </span>
    </div>
    <div style={{ opacity: 0.3 }}>
      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>{metric}</div>
      <div style={{ fontSize: '24px', fontWeight: '800', color: 'var(--primary)' }}>{value}</div>
      <div style={{ fontSize: '12px', color: 'var(--text-light)', marginTop: '4px' }}>{icon}</div>
    </div>
  </div>
);

const Analytics = () => {
  const [overview, setOverview] = useState(null);
  const [platforms, setPlatforms] = useState(null);
  const [channels, setChannels] = useState([]);
  const [videos, setVideos] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [channelAnalytics, setChannelAnalytics] = useState(null);
  const [strategy, setStrategy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [activePlatform, setActivePlatform] = useState('youtube');
  const [youtubeStats, setYoutubeStats] = useState(null);
  const [facebookStats, setFacebookStats] = useState(null);
  const [instagramStats, setInstagramStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [selectedLiveChannel, setSelectedLiveChannel] = useState({
    youtube: null, facebook: null, instagram: null, tiktok: null
  });

  useEffect(() => { fetchData(); fetchYoutubeStats(); }, []);

  const fetchPlatformStats = async (platform, channelId = null) => {
    try {
      const url = channelId
        ? `https://vantage-production-4475.up.railway.app/api/oauth/${platform}/stats?channel_id=${channelId}`
        : `https://vantage-production-4475.up.railway.app/api/oauth/${platform}/stats`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.success) {
        if (platform === 'youtube') setYoutubeStats(data.data);
        if (platform === 'facebook') setFacebookStats(data.data);
        if (platform === 'instagram') setInstagramStats(data.data);
        if (data.data.all_channels) {
          setSelectedLiveChannel(prev => ({
            ...prev,
            [platform]: channelId || data.data.db_channel_id
          }));
        }
      }
    } catch (error) {
      console.error(`Failed to fetch ${platform} stats`);
    }
  };

  const fetchYoutubeStats = async () => {
    setLoadingStats(true);
    try {
      await Promise.all([
        fetchPlatformStats('youtube'),
        fetchPlatformStats('facebook'),
        fetchPlatformStats('instagram')
      ]);
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchData = async () => {
    try {
      const [overviewRes, platformRes, channelsRes, videosRes] = await Promise.all([
        getOverview(),
        getPlatformAnalytics(),
        getChannels(),
        getVideos()
      ]);
      setOverview(overviewRes.data.data);
      setPlatforms(platformRes.data.data);
      setChannels(channelsRes.data.data || []);
      setVideos(videosRes.data.data || []);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchChannelAnalytics = async (channelId) => {
    try {
      const [analyticsRes, strategyRes] = await Promise.all([
        getChannelAnalytics(channelId),
        getUploadStrategy(channelId)
      ]);
      setChannelAnalytics(analyticsRes.data.data);
      setStrategy(strategyRes.data.data);
    } catch (error) {
      toast.error('Failed to load channel analytics');
    }
  };

  const handleChannelSelect = (channel) => {
    setSelectedChannel(channel);
    fetchChannelAnalytics(channel.id);
  };

  const overviewChartData = overview ? [
    { name: 'Published', value: overview.published_videos },
    { name: 'Scheduled', value: overview.scheduled_videos },
    { name: 'Drafts', value: overview.draft_videos },
    { name: 'Failed', value: overview.failed_videos }
  ] : [];

  const platformChartData = platforms ? Object.entries(platforms).map(([name, data]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    channels: data.active_channels,
    published: data.published_videos,
    pending: data.pending_uploads
  })) : [];

  const getRatingColor = (rating) => {
    if (rating === 'Excellent') return '#00c853';
    if (rating === 'Good') return '#3b82f6';
    if (rating === 'Average') return '#f59e0b';
    return '#ef4444';
  };

  const platformVideos = videos.filter(v => {
    const channel = channels.find(c => c.id === v.channel_id);
    return channel?.platform === activePlatform;
  });

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <MdBarChart /> },
    { id: 'platforms', label: 'Platforms', icon: <MdTrendingUp /> },
    { id: 'channels', label: 'Channels', icon: <MdStar /> },
    { id: 'live', label: 'Live Stats', icon: <MdVisibility /> }
  ];

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading"><div className="spinner"></div>Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1><span className="gradient-text">Analytics</span></h1>
        <p>Track performance across all your channels and platforms</p>
      </div>

      {/* Tab Navigation */}
      <div style={{
        display: 'flex', gap: '8px', marginBottom: '24px',
        background: 'var(--bg-card)', padding: '8px',
        borderRadius: '14px', border: '1px solid var(--border)'
      }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              flex: 1, display: 'flex', alignItems: 'center',
              justifyContent: 'center', gap: '8px',
              padding: '10px 16px', borderRadius: '10px',
              border: 'none', cursor: 'pointer',
              background: activeTab === tab.id ? 'var(--primary-gradient)' : 'transparent',
              color: activeTab === tab.id ? 'white' : 'var(--text-secondary)',
              fontWeight: activeTab === tab.id ? '700' : '400',
              fontSize: '14px', transition: 'all 0.2s ease',
              fontFamily: 'Inter, sans-serif',
              boxShadow: activeTab === tab.id ? 'var(--shadow-green)' : 'none'
            }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          <div className="grid-4" style={{ marginBottom: '24px' }}>
            {[
              { label: 'Total Channels', value: overview?.total_channels || 0, icon: <MdBarChart />, color: '#00c853' },
              { label: 'Total Videos', value: overview?.total_videos || 0, icon: <MdVideoLibrary />, color: '#00bcd4' },
              { label: 'Published', value: overview?.published_videos || 0, icon: <MdTrendingUp />, color: '#00c853' },
              { label: 'Scheduled', value: overview?.scheduled_videos || 0, icon: <MdStar />, color: '#f59e0b' },
            ].map((stat, i) => (
              <div key={i} className="stats-card">
                <div className="stats-icon" style={{ background: `${stat.color}20`, color: stat.color }}>
                  {stat.icon}
                </div>
                <div className="stats-info">
                  <h3 style={{ color: stat.color }}>{stat.value}</h3>
                  <p>{stat.label}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="grid-2" style={{ marginBottom: '24px' }}>
            <div className="card">
              <div className="card-header">
                <span className="card-title">Video Status Distribution</span>
              </div>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={overviewChartData} cx="50%" cy="50%" outerRadius={90}
                    dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                    {overviewChartData.map((entry, index) => (
                      <Cell key={index} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '8px' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="card">
              <div className="card-header">
                <span className="card-title">Platform Performance</span>
              </div>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={platformChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: 'var(--text-secondary)' }} />
                  <YAxis tick={{ fontSize: 12, fill: 'var(--text-secondary)' }} />
                  <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '8px' }} />
                  <Bar dataKey="published" fill="#00c853" radius={[4, 4, 0, 0]} name="Published" />
                  <Bar dataKey="pending" fill="#f59e0b" radius={[4, 4, 0, 0]} name="Pending" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Platform Breakdown Charts */}
          <h2 style={{
            fontSize: '20px', fontWeight: '700', marginBottom: '16px',
            color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px'
          }}>
            <MdBarChart style={{ color: 'var(--primary)' }} /> Platform Breakdown
          </h2>
          <div className="grid-2" style={{ marginBottom: '24px' }}>
            {platforms && Object.entries(platforms).map(([platform, data]) => {
              const config = {
                youtube: { icon: <FaYoutube style={{ color: '#ff0000' }} />, color: '#ff0000', label: 'YouTube' },
                facebook: { icon: <FaFacebook style={{ color: '#1877f2' }} />, color: '#1877f2', label: 'Facebook' },
                tiktok: { icon: <FaTiktok style={{ color: '#e0e0e0' }} />, color: '#e0e0e0', label: 'TikTok' },
                instagram: { icon: <FaInstagram style={{ color: '#e1306c' }} />, color: '#e1306c', label: 'Instagram' }
              }[platform];
              if (!config) return null;

              const chartData = [
                { name: 'Active', value: data.active_channels },
                { name: 'Published', value: data.published_videos },
                { name: 'Pending', value: data.pending_uploads },
                { name: 'Total', value: data.total_channels }
              ];

              return (
                <div key={platform} className="card">
                  <div className="card-header">
                    <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {config.icon} {config.label}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                    <ResponsiveContainer width="60%" height={180}>
                      <PieChart>
                        <Pie
                          data={chartData}
                          cx="50%" cy="50%"
                          innerRadius={45}
                          outerRadius={75}
                          dataKey="value"
                        >
                          {chartData.map((entry, index) => (
                            <Cell key={index} fill={
                              index === 0 ? config.color :
                              index === 1 ? '#00c853' :
                              index === 2 ? '#f59e0b' : '#64748b'
                            } />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '12px' }} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {[
                        { label: 'Active', value: data.active_channels, color: config.color },
                        { label: 'Published', value: data.published_videos, color: '#00c853' },
                        { label: 'Pending', value: data.pending_uploads, color: '#f59e0b' },
                        { label: 'Total', value: data.total_channels, color: '#64748b' }
                      ].map((stat, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: stat.color }} />
                            <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{stat.label}</span>
                          </div>
                          <span style={{ fontWeight: '700', fontSize: '16px', color: stat.color }}>{stat.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Platforms Tab */}
      {activeTab === 'platforms' && (
        <>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
            {Object.entries(platformConfig).map(([platform, config]) => (
              <button
                key={platform}
                onClick={() => setActivePlatform(platform)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '10px 20px', borderRadius: '10px',
                  border: `2px solid ${activePlatform === platform ? config.border : 'var(--border)'}`,
                  background: activePlatform === platform ? config.bg : 'var(--bg-card)',
                  cursor: 'pointer', transition: 'all 0.2s ease',
                  fontFamily: 'Inter, sans-serif', fontWeight: activePlatform === platform ? '700' : '400',
                  color: activePlatform === platform ? config.color : 'var(--text-secondary)'
                }}
              >
                {config.icon} {config.label}
              </button>
            ))}
          </div>

          {platforms && platformConfig[activePlatform] && (
            <>
              <div className="grid-4" style={{ marginBottom: '24px' }}>
                {[
                  { label: 'Total Channels', value: platforms[activePlatform]?.total_channels || 0 },
                  { label: 'Active Channels', value: platforms[activePlatform]?.active_channels || 0 },
                  { label: 'Published', value: platforms[activePlatform]?.published_videos || 0 },
                  { label: 'Pending', value: platforms[activePlatform]?.pending_uploads || 0 }
                ].map((stat, i) => (
                  <div key={i} style={{
                    background: platformConfig[activePlatform].bg,
                    border: `1px solid ${platformConfig[activePlatform].border}`,
                    borderRadius: '14px', padding: '20px', textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '32px', fontWeight: '800', color: platformConfig[activePlatform].color }}>
                      {stat.value}
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>

              <div className="card">
                <div className="card-header">
                  <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {platformConfig[activePlatform].icon}
                    {platformConfig[activePlatform].label} Videos
                  </span>
                  <span className="badge badge-info">{platformVideos.length} videos</span>
                </div>
                {platformVideos.length === 0 ? (
                  <div className="empty-state">
                    <p>No videos found for this platform</p>
                  </div>
                ) : (
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Title</th>
                          <th>Status</th>
                          <th>AI Used</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {platformVideos.slice(0, 10).map(video => (
                          <tr key={video.id}>
                            <td style={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {video.youtube_title || video.title || 'Untitled'}
                            </td>
                            <td>
                              <span className={`badge badge-${video.status === 'published' ? 'success' : video.status === 'scheduled' ? 'warning' : 'secondary'}`}>
                                {video.status}
                              </span>
                            </td>
                            <td>
                              {video.claude_ai_used
                                ? <span className="ai-badge"><MdAutoAwesome /> AI</span>
                                : <span style={{ color: 'var(--text-light)', fontSize: '13px' }}>Manual</span>}
                            </td>
                            <td style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                              {new Date(video.created_at).toLocaleDateString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* Channels Tab */}
      {activeTab === 'channels' && (
        <div className="grid-2">
          <div className="card">
            <div className="card-header">
              <span className="card-title">Select Channel to Analyse</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {channels.map(channel => {
                const config = platformConfig[channel.platform];
                return (
                  <button
                    key={channel.id}
                    onClick={() => handleChannelSelect(channel)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px',
                      padding: '14px 16px', borderRadius: '12px',
                      border: `1px solid ${selectedChannel?.id === channel.id ? 'var(--primary)' : 'var(--border)'}`,
                      background: selectedChannel?.id === channel.id ? 'rgba(0,200,83,0.08)' : 'var(--bg-dark)',
                      cursor: 'pointer', transition: 'all 0.2s ease',
                      fontFamily: 'Inter, sans-serif', textAlign: 'left'
                    }}
                  >
                    {config?.icon}
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '14px', color: 'var(--text-primary)' }}>
                        {channel.name}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', textTransform: 'capitalize' }}>
                        {channel.platform}
                      </div>
                    </div>
                    {selectedChannel?.id === channel.id && (
                      <span style={{ marginLeft: 'auto', color: 'var(--primary)', fontSize: '12px', fontWeight: '700' }}>
                        Selected
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            {strategy ? (
              <>
                <div className="card" style={{ marginBottom: '20px' }}>
                  <div className="card-header">
                    <span className="card-title">Performance Score</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
                    <div style={{
                      width: '80px', height: '80px', borderRadius: '50%',
                      background: `conic-gradient(${getRatingColor(strategy.rating)} ${strategy.performance_score * 3.6}deg, var(--bg-dark) 0deg)`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <div style={{
                        width: '60px', height: '60px', borderRadius: '50%',
                        background: 'var(--bg-card)',
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center'
                      }}>
                        <span style={{ fontSize: '16px', fontWeight: '800', color: getRatingColor(strategy.rating) }}>
                          {strategy.performance_score}%
                        </span>
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '22px', fontWeight: '800', color: getRatingColor(strategy.rating) }}>
                        {strategy.rating}
                      </div>
                      <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                        {strategy.total_videos} total videos
                      </div>
                    </div>
                  </div>

                  <div style={{
                    background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)',
                    borderRadius: '10px', padding: '14px', marginBottom: '16px'
                  }}>
                    <div style={{ fontSize: '12px', fontWeight: '700', color: '#f59e0b', marginBottom: '6px' }}>
                      RECOMMENDATION
                    </div>
                    <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                      {strategy.recommendation}
                    </p>
                  </div>

                  <div className="grid-2">
                    <div style={{ textAlign: 'center', padding: '14px', background: 'var(--bg-dark)', borderRadius: '10px' }}>
                      <div style={{ fontWeight: '700', fontSize: '16px', color: 'var(--primary)' }}>
                        {strategy.suggested_upload_frequency}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-light)', marginTop: '4px' }}>
                        Upload Frequency
                      </div>
                    </div>
                    <div style={{ textAlign: 'center', padding: '14px', background: 'var(--bg-dark)', borderRadius: '10px' }}>
                      <div style={{ fontWeight: '700', fontSize: '16px', color: '#00c853' }}>
                        {strategy.optimal_upload_time}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-light)', marginTop: '4px' }}>
                        Best Upload Time
                      </div>
                    </div>
                  </div>
                </div>

                {channelAnalytics && (
                  <div className="card">
                    <div className="card-header">
                      <span className="card-title">Channel Stats</span>
                    </div>
                    {[
                      { label: 'Published', value: channelAnalytics.published, color: '#00c853' },
                      { label: 'Scheduled', value: channelAnalytics.scheduled, color: '#f59e0b' },
                      { label: 'Drafts', value: channelAnalytics.drafts, color: '#64748b' },
                      { label: 'Failed', value: channelAnalytics.failed, color: '#ef4444' },
                      { label: 'AI Generated', value: channelAnalytics.ai_generated_metadata, color: 'var(--primary)' },
                      { label: 'Last 30 Days', value: channelAnalytics.recent_uploads_30_days, color: '#00bcd4' },
                    ].map((stat, i) => (
                      <div key={i} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '12px 0', borderBottom: '1px solid var(--border)'
                      }}>
                        <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>{stat.label}</span>
                        <span style={{ fontWeight: '700', fontSize: '18px', color: stat.color }}>{stat.value}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="card" style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ textAlign: 'center' }}>
                  <MdBarChart style={{ fontSize: '48px', color: 'var(--primary)', opacity: 0.3, marginBottom: '12px' }} />
                  <p style={{ color: 'var(--text-secondary)' }}>Select a channel to view analytics</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Live Stats Tab */}
      {activeTab === 'live' && (
        <>
          {Object.entries(platformConfig).map(([platform, config]) => {
            const statsMap = {
              youtube: youtubeStats,
              facebook: facebookStats,
              instagram: instagramStats,
              tiktok: null
            };
            const stats = statsMap[platform];
            const allChannels = stats?.all_channels || [];
            const isConnected = !!stats;

            return (
              <div key={platform} style={{
                background: config.bg, border: `1px solid ${config.border}`,
                borderRadius: '16px', padding: '20px', marginBottom: '16px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {config.icon}
                    <span style={{ fontWeight: '700', fontSize: '18px', color: config.color }}>
                      {config.label} Live Stats
                    </span>
                  </div>
                  {isConnected && allChannels.length > 1 && (
                    <select
                      value={selectedLiveChannel[platform] || ''}
                      onChange={(e) => fetchPlatformStats(platform, e.target.value)}
                      style={{
                        padding: '8px 12px', borderRadius: '8px', fontSize: '13px',
                        background: 'var(--bg-card)', border: '1px solid var(--border)',
                        color: 'var(--text-primary)', cursor: 'pointer',
                        fontFamily: 'Inter, sans-serif'
                      }}
                    >
                      {allChannels.map(ch => (
                        <option key={ch.id} value={ch.id}>{ch.name}</option>
                      ))}
                    </select>
                  )}
                </div>

                {isConnected ? (
                  <div className="grid-4">
                    {platform === 'youtube' && [
                      { metric: 'Channel', value: stats.channel_name, icon: '✅' },
                      { metric: 'Subscribers', value: parseInt(stats.subscribers || 0).toLocaleString(), icon: '👥' },
                      { metric: 'Total Views', value: parseInt(stats.total_views || 0).toLocaleString(), icon: '👁' },
                      { metric: 'Total Videos', value: parseInt(stats.total_videos || 0).toLocaleString(), icon: '🎬' },
                      { metric: 'Views Last 48hrs', value: parseInt(stats.views_48hr || 0).toLocaleString(), icon: '📈' }
                    ].map((stat, i) => (
                      <div key={i} style={{
                        padding: '16px', background: 'var(--bg-card)',
                        borderRadius: '12px', border: '1px solid var(--border)'
                      }}>
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                          {stat.icon} {stat.metric}
                        </div>
                        <div style={{ fontSize: '18px', fontWeight: '800', color: config.color }}>
                          {stat.value}
                        </div>
                      </div>
                    ))}
                    {platform === 'facebook' && [
                      { metric: 'Page', value: stats.channel_name, icon: '✅' },
                      { metric: 'Followers', value: parseInt(stats.followers || 0).toLocaleString(), icon: '👥' },
                      { metric: 'Page Fans', value: parseInt(stats.fans || 0).toLocaleString(), icon: '❤️' },
                      { metric: 'Video Views Today', value: parseInt(stats.video_views_today || 0).toLocaleString(), icon: '▶️' }
                    ].map((stat, i) => (
                      <div key={i} style={{
                        padding: '16px', background: 'var(--bg-card)',
                        borderRadius: '12px', border: '1px solid var(--border)'
                      }}>
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                          {stat.icon} {stat.metric}
                        </div>
                        <div style={{ fontSize: '18px', fontWeight: '800', color: config.color }}>
                          {stat.value}
                        </div>
                      </div>
                    ))}
                    {platform === 'instagram' && [
                      { metric: 'Account', value: stats.channel_name, icon: '✅' },
                      { metric: 'Followers', value: parseInt(stats.followers || 0).toLocaleString(), icon: '👥' },
                      { metric: 'Total Posts', value: parseInt(stats.media_count || 0).toLocaleString(), icon: '📸' }
                    ].map((stat, i) => (
                      <div key={i} style={{
                        padding: '16px', background: 'var(--bg-card)',
                        borderRadius: '12px', border: '1px solid var(--border)'
                      }}>
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                          {stat.icon} {stat.metric}
                        </div>
                        <div style={{ fontSize: '18px', fontWeight: '800', color: config.color }}>
                          {stat.value}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    textAlign: 'center', padding: '32px',
                    background: 'var(--bg-dark)', borderRadius: '12px',
                    border: '1px dashed var(--border)'
                  }}>
                    <div style={{ fontSize: '32px', marginBottom: '12px' }}>🔗</div>
                    <div style={{ fontWeight: '700', fontSize: '14px', color: 'var(--text-primary)', marginBottom: '8px' }}>
                      Connect {config.label} to see live stats
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
                      Go to Platform Manager and connect your {config.label} account
                    </div>
                    <button
                      onClick={() => window.location.href = '/?page=channels'}
                      className="btn btn-primary btn-sm">
                      Connect {config.label}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

export default Analytics;